import { SLEEK_TOPBAR } from "pages/DesignCanvasAgentPage/components/sleek";
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useFloating, offset, flip, shift, autoUpdate } from "@floating-ui/react";
import { useTranslation } from "react-i18next";
import { Megaphone, ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { IconDoubleChecked, IconRightArrow } from "icons";
import { cn } from "utils/cn";
import { AnalyticsEvent } from "utils";
import { PATHS } from "router/config/paths";
import { useAppDispatch, useAppSelector } from "store";
import { setWhatsNewUnreadCount } from "store/whatsNewSlice";
import whatsNewService from "api/whatsNew/whatsNew.service";
import { IWhatsNewItem } from "api/whatsNew/whatsNew.type";
import { formatRelativeTime } from "./whatsNew.data";
import useOpenWhatsNewItem from "./useOpenWhatsNewItem";
import groupWhatsNewItems from "./groupWhatsNewItems";
import ReleaseNotesCard from "./ReleaseNotesCard";
import WhatsNewCardMedia from "./WhatsNewCardMedia";

// Panel shows the most-recent slice of CARDS (one UTC day's improvements + fixes count as one); the rest
// live on the See-all page.
const PANEL_LIMIT = 10;

// Keep this in sync with the avatar / notifications dropdown offset so all panels hang at the same height.
const PANEL_OFFSET = 12;

// What's New announcements button (REF-6263 split): sits next to Ask Agent as an icon+label button and
// opens the announcements feed. Notifications moved out to their own bell (NotificationsMenu).
const WhatsNewMenu = () => {
  const { t, i18n: i18nInstance } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const openItem = useOpenWhatsNewItem();
  const [open, setOpen] = useState(false);

  // Feed + unread come from the boot fetch (Layout → useWhatsNewBoot). Items are already newest-first.
  const items = useAppSelector((s) => s.whatsNew.items);
  const unreadCount = useAppSelector((s) => s.whatsNew.unreadCount);
  // REF-6550: display-only grouping — the raw rows stay in Redux for the tour lookup.
  const entries = useMemo(() => groupWhatsNewItems(items).slice(0, PANEL_LIMIT), [items]);

  const { refs, floatingStyles } = useFloating({
    placement: "bottom-end",
    middleware: [offset(PANEL_OFFSET), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const handleToggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        AnalyticsEvent("whats_new_open", "", "click");

        // Mark everything read the moment the panel opens (server-side watermark). Optimistically
        // clear the dot, then reconcile with the server's returned count.
        if (unreadCount > 0) {
          dispatch(setWhatsNewUnreadCount(0));
          whatsNewService
            .markSeen()
            .then((res) => {
              // Service returns the body directly — read `unread_count`, not `data.unread_count`.
              if (res && typeof res.unread_count === "number") dispatch(setWhatsNewUnreadCount(res.unread_count));
            })
            .catch(() => undefined);
        }
      }

      return next;
    });
  };

  const handleItemClick = (item: IWhatsNewItem) => {
    setOpen(false);
    openItem(item, "panel");
  };

  const goSeeAll = () => {
    AnalyticsEvent("whats_new_see_all", "", "click");
    setOpen(false);
    navigate(PATHS.WHATS_NEW);
  };

  const handleMarkAllRead = () => {
    dispatch(setWhatsNewUnreadCount(0));
    whatsNewService
      .markSeen()
      .then((res) => {
        // Service returns the body directly — read `unread_count`, not `data.unread_count`.
        if (res && typeof res.unread_count === "number") dispatch(setWhatsNewUnreadCount(res.unread_count));
      })
      .catch(() => undefined);
  };

  // Close on outside click (the panel is portaled, so include it as "inside").
  useEffect(() => {
    if (!open) return;

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = refs.reference.current instanceof HTMLElement && refs.reference.current.contains(target);
      const inPopover = refs.floating.current instanceof HTMLElement && refs.floating.current.contains(target);
      if (!inTrigger && !inPopover) setOpen(false);
    };

    document.addEventListener("mousedown", handleMouseDown);

    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open, refs.reference, refs.floating]);

  return (
    <div className="relative">
      <button
        ref={refs.setReference}
        onClick={handleToggle}
        className={cn(
          "group relative flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-medium text-trueBlack transition-[filter] hover:brightness-[0.97] dark:text-white",
          SLEEK_TOPBAR,
          open && "brightness-[0.94]"
        )}
        title={t("common:whats_new.title")}
        aria-label={t("common:whats_new.title")}
        aria-expanded={open}
      >
        <Megaphone className="h-[18px] w-[18px]" strokeWidth={1.75} />
        <span>{t("common:whats_new.title")}</span>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex-center h-4 min-w-4 rounded-full bg-[#FF3B30] px-[3px] text-[10px] font-semibold text-trueWhite ring-2 ring-trueWhite dark:ring-black group-hover:ring-black-200">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: 999999 }}
            className="flex flex-col w-[380px] max-h-[75vh] rounded-xl bg-trueWhite dark:bg-black border border-black-300 shadow-xl overflow-hidden"
          >
            <div className="border-b border-black-300 px-4 py-3 text-[14px] font-semibold text-trueBlack dark:text-white">{t("common:whats_new.tabs.whats_new")}</div>

            <div className="flex flex-col gap-1 overflow-y-auto p-2">
              {items.length === 0 ? (
                <div className="px-4 py-10 text-center text-[13px] text-grey">{t("common:whats_new.empty")}</div>
              ) : (
                entries.map((entry) => {
                  if (entry.type === "release") return <ReleaseNotesCard key={entry.key} items={entry.items} publishedAt={entry.publishedAt} />;

                  const { item } = entry;

                  return (
                    <button
                      key={entry.key}
                      onClick={() => handleItemClick(item)}
                      onMouseEnter={(e) => {
                        const v = e.currentTarget.querySelector("video");
                        if (v) v.play().catch(() => undefined);
                      }}
                      onMouseLeave={(e) => {
                        const v = e.currentTarget.querySelector("video");
                        if (v) v.pause();
                      }}
                      className="group flex w-full flex-col gap-2 rounded-xl px-3 py-3 text-left transition-colors hover:bg-black-200/60"
                    >
                      <div className="flex min-w-0 items-center gap-1">
                        <span className="min-w-0 text-[14px] font-semibold text-trueBlack [overflow-wrap:anywhere] dark:text-white">{item.title}</span>
                        <ArrowUpRight className="h-3.5 w-3.5 flex-shrink-0 -translate-x-1 text-grey opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" strokeWidth={2} />
                      </div>
                      <span className="custom-line-clamp-2 text-[13px] text-grey [overflow-wrap:anywhere]">{item.description}</span>
                      <WhatsNewCardMedia media={item.media} />
                      <span className="text-[12px] text-grey/80">{formatRelativeTime(item.published_at || item.date, i18nInstance.language)}</span>
                    </button>
                  );
                })
              )}
            </div>

            {items.length > 0 && (
              <div className="flex items-center gap-2 border-t border-black-300 p-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-[13px] font-medium text-trueBlack dark:text-white hover:bg-black-200/60 transition-colors"
                  >
                    <IconDoubleChecked className="h-4 w-4" />
                    {t("notifications:page.mark_all_read")}
                  </button>
                )}
                <button
                  onClick={goSeeAll}
                  className="ml-auto flex items-center gap-1.5 rounded-lg px-3 py-2 text-[13px] font-medium text-trueBlack dark:text-white hover:bg-black-200/60 transition-colors"
                >
                  {t("common:whats_new.see_all")}
                  <IconRightArrow className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default WhatsNewMenu;
